import React from "react";
import { Box, Flex, Grid, Image, Input, Text, Tooltip } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { useVirtualizer } from "@tanstack/react-virtual";
import { produce } from "immer";
import {
  FlatSheetKey,
  inflatedTabsFamily,
  InflatedProduct,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import useInflatedTabs, {
  PriceConfig,
} from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { ProductPrice } from "@/features/types";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useOrderSheetProductLikes from "@/features/order-sheet-sets/hooks/use-order-sheet-product-likes";
import useOrderSheetCurrency from "@/features/order-sheet-sets/hooks/use-order-sheet-currency";
import { OrderSheetTabBottomBar } from "@/features/order-sheet-sets/order-sheet-tab-bottom-bar";
import useOrderSheetSetPagePersistedQuery from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import { isSheetTabFilterActive } from "@/features/order-sheet-sets/sheet-tab-filter";
import CustomIcon from "../../../public/icons-v1_1";
import {
  LineSheetProductMarketPrice,
  LineSheetSetMarketPricing,
} from "@/features/order-sheet-sets/hooks/use-line-sheet-set-market-prices";
import OrderSheetMarketComparison from "@/features/order-sheet-sets/order-sheet-market-comparison";
import { PathPickProductCardComparisonLayout } from "@/features/pathpick/types";
import OrderSheetMarketSizeModal from "@/features/order-sheet-sets/order-sheet-market-size-modal";

// 카드당 사진 개수 — 사용자가 고른다. 개수에 따라 열 수가 정해진다.
export const IMAGE_COUNT_OPTIONS = [1, 2, 4, 6] as const;
export type ImageCount = (typeof IMAGE_COUNT_OPTIONS)[number];
const IMAGE_COLUMNS: Record<ImageCount, number> = { 1: 1, 2: 2, 4: 2, 6: 3 };
export const DEFAULT_IMAGE_COUNT: ImageCount = 4;
// 사진 개수별 한 장 크기 — 2·4장은 한 줄에 카드 4개가 들어가도록 작게 잡는다
// (1장은 카드가 어차피 좁아 크게, 6장은 넓은 화면에서 보는 모드).
const IMAGE_SIZES: Record<ImageCount, number> = {
  1: 200,
  2: 140,
  4: 140,
  6: 140,
};
// 카드 안·카드 사이 간격을 이 값 하나로 맞춘다.
const CARD_GAP = 6;
// 확대는 훑어보는 걸 방해하면 안 된다 — 지나가는 마우스에는 안 뜨게 잠깐 머문 뒤에 띄운다.
const ZOOM_OPEN_DELAY = 500;
// 호버 시 띄우는 확대 이미지 크기.
const ZOOM_IMAGE_SIZE = 560;
// 한 장만 보여주는 모드에서 호버로 여러 장을 띄울 때의 한 칸 크기·최대 장수.
const ZOOM_GRID_IMAGE_SIZE = 240;
const ZOOM_GRID_MAX_COUNT = 6;
// 카드 안쪽 좌우 여백(12px * 2). 카드 최소 폭은 이미지 열 수에 따라 계산한다.
const CARD_PADDING = 24;
// 목록 좌우 여백.
const CONTENT_PADDING = 16;
// 사진이 적어도 옵션 표(옵션명 / 재고 / 수량)가 눌리지 않는 최소 폭.
const MIN_CARD_WIDTH_FLOOR = 180;
// 시세 비교 블록의 최소 카드 폭 — 패스픽 상품 카드와 같은 값.
const MARKET_MIN_CARD_WIDTH: Record<PathPickProductCardComparisonLayout, number> =
  { side: 240, stack: 200 };

interface Props {
  sheetKey: FlatSheetKey;
  tabIndex: number;
  isLoading?: boolean;
  // 주문 수량 입력 허용 여부. 그리드의 isOrderEditable과 같은 값을 넘긴다.
  isEditable?: boolean;
  // 컨디션 재계산에 쓰는 수수료 설정 — 그리드와 같은 값을 넘겨야 가격이 어긋나지 않는다.
  priceConfig?: PriceConfig;
  // 수량이 바뀌면 페이지가 '편집 중'으로 표시하고 저장 버튼을 열도록 알린다.
  onUpdatingInflatedProductComplete?: (isCompleted: boolean) => void;
  // 상단 일괄 적용(컨디션·IVA)이 쓰는 선택 상태 — 그리드와 같은 값을 공유한다.
  selectedProductIds?: number[];
  onSelectedProductIdsChange?: (productIds: number[]) => void;
  // 선택했을 때 전체 선택 옆에 붙는 액션(컨디션 적용·다운로드). 페이지가 넘겨준다.
  selectionActions?: React.ReactNode;
  // 아래 넷은 페이지 툴바의 설정(톱니)에서 고른다 — 값도 그쪽이 쥔다.
  imageCount?: ImageCount;
  showPriceDetail?: boolean;
  isAllExpanded?: boolean;
  /** 별을 눌러 둔 상품만 볼지 — 자세히 보기와 같은 토글을 공유한다. */
  likedOnly?: boolean;
  // 시트 통화(lineSheetSet.currency). 하단 주문 통계의 합계 표기·원화 환산에 쓴다.
  currency?: string | null;
  /** 상품 id → 크림·포이즌 시세. 패스픽 멤버가 아니면 undefined 이고 카드에 시세가 없다. */
  marketPriceByProductId?: Map<number, LineSheetProductMarketPrice>;
  /** 예상 매입가 계산 재료(환율·정액 수수료) — 공급가는 카드의 현재 값을 쓴다. */
  marketPricing?: LineSheetSetMarketPricing;
  /** 시세 비교 배치 — 나란히(side) / 위아래(stack). 카드 보기 설정에서 고른다. */
  marketComparisonLayout?: PathPickProductCardComparisonLayout;
}

interface InfoFieldProps {
  label: string;
  value: string;
  isEmphasized?: boolean;
}

function InfoField({ label, value, isEmphasized }: InfoFieldProps) {
  return (
    <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text
        textStyle={"body-sm-bold"}
        color={isEmphasized ? "#1272EF" : "#1D1D1B"}
        noOfLines={1}
        wordBreak={"break-all"}
      >
        {value}
      </Text>
    </Flex>
  );
}

interface GalleryImageProps {
  url: string;
  alt: string;
  size: number;
  isAi?: boolean;
  // 카드에 한 장만 보여줄 때, 호버로 나머지까지 한 번에 보여주기 위한 목록.
  // 두 장 이상 넘어오면 확대 대신 격자로 띄운다.
  zoomImages?: { url: string; isAi: boolean }[];
  // 여러 장을 나란히 둘 때는 어디까지가 한 장인지 테두리로 끊어 준다.
  showBorder?: boolean;
}

function AiBadge() {
  return (
    <Box
      position={"absolute"}
      top={"4px"}
      left={"4px"}
      padding={"1px 6px"}
      borderRadius={"4px"}
      background={"#1272EF"}
      pointerEvents={"none"}
    >
      <Text textStyle={"caption-bold"} color={"#FFFFFF"}>
        AI
      </Text>
    </Box>
  );
}

function GalleryImage({
  url,
  alt,
  size,
  isAi,
  zoomImages,
  showBorder = false,
}: GalleryImageProps) {
  const hasMultiZoom = (zoomImages?.length ?? 0) > 1;
  return (
    <Tooltip
      placement={"right"}
      hasArrow={true}
      // 훑는 동안 확대가 따라다니면 방해가 된다 — 한동안 머물렀을 때만 띄운다.
      openDelay={ZOOM_OPEN_DELAY}
      // 확대 이미지가 툴팁을 꽉 채우므로 모서리를 이미지와 같은 8px로 맞춘다.
      // 배경이 어두우면 상품 사진이 잘 안 보여서 흰색 + 얇은 테두리로 둔다.
      backgroundColor={"#FFFFFF"}
      border={"0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      boxShadow={"0px 4px 8px 0px rgba(0, 0, 0, 0.20)"}
      maxWidth={"auto"}
      padding={"0"}
      margin={"0"}
      modifiers={[{ name: "preventOverflow", options: { padding: 8 } }]}
      label={
        hasMultiZoom ? (
          <Grid
            templateColumns={`repeat(${Math.min(
              zoomImages!.length,
              3
            )}, ${ZOOM_GRID_IMAGE_SIZE}px)`}
            gap={"4px"}
            padding={"4px"}
          >
            {zoomImages!.map((image, index) => (
              <Box key={`zoom_${index}`} position={"relative"}>
                <Image
                  src={image.url}
                  boxSize={`${ZOOM_GRID_IMAGE_SIZE}px`}
                  objectFit={"contain"}
                  background={"#FFFFFF"}
                  // 흰 배경에 여러 장이 붙어 나와서 장끼리 경계가 필요하다.
                  border={"0.5px solid #D4D4D1"}
                  borderRadius={"8px"}
                  alt={alt}
                />
                {image.isAi && <AiBadge />}
              </Box>
            ))}
          </Grid>
        ) : (
          <Box position={"relative"}>
            <Image
              src={url}
              boxSize={`${ZOOM_IMAGE_SIZE}px`}
              objectFit={"contain"}
              background={"#FFFFFF"}
              borderRadius={"8px"}
              alt={alt}
            />
            {isAi && <AiBadge />}
          </Box>
        )
      }
    >
      <Box position={"relative"} flexShrink={0}>
        <Image
          // 크게 보는 뷰라 원본을 쓴다(편집 그리드는 작은 셀이라 thumbnail_ 파일을 쓴다).
          src={url}
          // 상품이 많으면 원본 수백 장을 한 번에 받느라 느려진다.
          // 화면에 들어올 때만 받고, 디코딩도 렌더를 막지 않게 한다.
          loading={"lazy"}
          decoding={"async"}
          boxSize={`${size}px`}
          objectFit={"contain"}
          background={"#FFFFFF"}
          borderRadius={"8px"}
          border={showBorder ? "0.5px solid #D4D4D1" : undefined}
          alt={alt}
        />
        {isAi && <AiBadge />}
      </Box>
    </Tooltip>
  );
}

// 사진 개수 아이콘 — 칸 배치가 곧 설명이라 숫자를 따로 적지 않아도 알아본다.
function ImageCountIcon({ count }: { count: ImageCount }) {
  const columns = IMAGE_COLUMNS[count];
  const rows = Math.ceil(count / columns);
  const AREA = 18;
  const GAP = 2;
  const width = (AREA - (columns - 1) * GAP) / columns;
  const height = (AREA - (rows - 1) * GAP) / rows;
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
      {Array.from({ length: count }, (_, index) => (
        <rect
          key={index}
          x={3 + (index % columns) * (width + GAP)}
          y={3 + Math.floor(index / columns) * (height + GAP)}
          width={width}
          height={height}
          rx={1}
        />
      ))}
    </svg>
  );
}

export function ImageCountButton({
  count,
  isActive,
  label,
  onClick,
}: {
  count: ImageCount;
  isActive: boolean;
  label?: string;
  onClick: () => void;
}) {
  return (
    <Flex
      as={"button"}
      alignItems={"center"}
      justifyContent={"center"}
      boxSize={"24px"}
      borderRadius={"4px"}
      border={isActive ? "0.5px solid #1272EF" : "0.5px solid #D4D4D1"}
      background={isActive ? "#F0F9FF" : "#FCFCFB"}
      color={isActive ? "#1272EF" : "#8F8F8C"}
      _hover={{ borderColor: "#1272EF" }}
      aria-label={label ?? String(count)}
      title={label ?? String(count)}
      onClick={onClick}
    >
      <ImageCountIcon count={count} />
    </Flex>
  );
}

interface GalleryCardProps {
  product: InflatedProduct;
  imageCount: ImageCount;
  imageColumns: number;
  imageRows: number;
  imageSize: number;
  isExpanded: boolean;
  isSelected: boolean;
  isLiked: boolean;
  onToggleLike: (productId: number) => void;
  // 도매가·소매가·컨디션까지 볼지 — 끄면 공급가 한 줄만 남는다.
  showPriceDetail: boolean;
  // 선택을 쓰지 않는 화면이면 undefined — 체크박스를 그리지 않는다.
  onToggleSelected?: (productId: number) => void;
  // 상단에서 전체를 펼쳤으면 카드마다 화살표를 두지 않는다.
  showToggle: boolean;
  isEditable: boolean;
  onToggle: (productId: number) => void;
  onQuantityChange: (
    product: InflatedProduct,
    optionIndex: number,
    rawValue: string
  ) => void;
  onTakeAll: (product: InflatedProduct) => void;
  onCopySku: (sku: string | null) => void;
  // 크림·포이즌 시세 — 매칭이 없거나 멤버가 아니면 undefined 라 블록 자체를 그리지 않는다.
  marketPrice?: LineSheetProductMarketPrice;
  onOpenMarketSizes?: (product: InflatedProduct) => void;
  marketComparisonLayout: PathPickProductCardComparisonLayout;
  marketPricing?: LineSheetSetMarketPricing;
}

// 카드 하나. memo 로 감싸 두면 한 상품의 수량을 고쳐도 나머지 카드는 다시 그리지 않는다
// (immer 가 건드리지 않은 상품 객체의 참조를 그대로 두기 때문에 얕은 비교로 걸러진다).
const GalleryCard = React.memo(function GalleryCard({
  product,
  imageCount,
  imageColumns,
  imageRows,
  imageSize,
  isExpanded,
  isSelected,
  isLiked,
  onToggleLike,
  showPriceDetail,
  onToggleSelected,
  showToggle,
  isEditable,
  onToggle,
  onQuantityChange,
  onTakeAll,
  onCopySku,
  marketPrice,
  onOpenMarketSizes,
  marketComparisonLayout,
  marketPricing,
}: GalleryCardProps) {
  const { t, tTitle, tCurrencyString } = useI18nHelper();
  // 원화 보기 환산은 그리드와 같은 훅을 쓴다 — 단가가 양쪽에서 똑같이 보여야 한다.
  const { convert } = useOrderSheetCurrency();
  const formatPrice = (amount: number, currency: string) => {
    const converted = convert(amount, currency);
    return tCurrencyString(converted.amount, {
      type: "SYMBOL",
      code: converted.currency,
    });
  };

  const price = (value: ProductPrice | null) =>
    value ? formatPrice(value.amount, value.currency) : "-";

  // 이름과 값이 좌우로 갈라져야 여러 줄일 때 값이 한 열로 정렬돼 읽힌다.
  const priceRow = (
    label: string,
    value: string,
    isEmphasized: boolean = false
  ) => (
    <Flex justifyContent={"space-between"} gap={`${CARD_GAP}px`} minWidth={0}>
      <Text
        textStyle={isEmphasized ? "caption-bold" : "caption-medium"}
        color={isEmphasized ? "#1D1D1B" : "#8F8F8C"}
        flexShrink={0}
        noOfLines={1}
      >
        {label}
      </Text>
      <Text
        textStyle={isEmphasized ? "caption-bold" : "caption-medium"}
        color={isEmphasized ? "#1D1D1B" : "#8F8F8C"}
        textAlign={"right"}
        noOfLines={1}
      >
        {value}
      </Text>
    </Flex>
  );

  const totalQuantity = product.orderQuantityWithOptionList.reduce(
    (sum, option) => sum + (option.quantity || 0),
    0
  );

  // 주문 금액 = 공급가 × 총 주문 수량. 공급가가 없으면 계산할 수 없어 "-".
  const orderedAmount = () => {
    const supplyPrice = product.supplyPrice;
    if (!supplyPrice) return "-";
    return formatPrice(supplyPrice.amount * totalQuantity, supplyPrice.currency);
  };

  // "DISCOUNT 10%" + IVA 적용 시 계산식까지. IVA는 DISCOUNT에서만 의미가 있다.
  const conditionLabel = () => {
    const condition = product.priceAdjustedBy;
    if (!condition?.by) return "-";
    const base = `${condition.by} ${condition.value ?? 0}%`;
    if (condition.by !== "DISCOUNT") return base;
    // 공급가 뒤 괄호 안에 들어가므로 IVA 는 괄호를 겹치지 않게 가운뎃점으로 잇는다.
    if (condition.applyIva === false) return `${base} · IVA -`;
    return `${base} · IVA ${condition.ivaValue || "/1.22"}`;
  };

  // 모든 옵션이 이미 재고만큼(또는 그 이상) 담겨 있으면 더 담을 게 없다.
  const stockList =
    product.newStockWithOptionList || product.stockQuantityWithOptionList;
  const isTakenAll =
    !stockList ||
    stockList.length === 0 ||
    stockList.every((stockOption, index) => {
      const ordered = product.orderQuantityWithOptionList[index]?.quantity ?? 0;
      return ordered >= stockOption.quantity;
    });

  // 원본을 먼저 채우고 남는 자리에 AI — 2장씩 두 줄, 최대 4장.
  const allImages = [
    ...product.imageUrls.map((url) => ({ url, isAi: false })),
    ...product.alternativeImageUrls.map((url) => ({ url, isAi: true })),
  ];
  const cardImages = allImages.slice(0, imageCount);
  // 한 장만 보여줄 때는 호버로 나머지까지 훑을 수 있게 목록을 넘긴다.
  const zoomImages =
    imageCount === 1 ? allImages.slice(0, ZOOM_GRID_MAX_COUNT) : undefined;

  return (
    <Flex
      key={product.id}
      flexDirection={"column"}
      gap={`${CARD_GAP}px`}
      padding={"12px"}
      border={isSelected ? "1px solid #1272EF" : "0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      // 고른 카드는 테두리만으로는 덜 보여서 배경까지 바꾼다.
      background={isSelected ? "#F7F9FA" : "#FFFFFF"}
      alignSelf={"start"}
      position={"relative"}
      cursor={onToggleSelected ? "pointer" : "default"}
      // 카드 아무 데나 눌러도 선택된다. 안쪽 조작 요소(별·SKU 복사·가격 줄·수량 입력 등)는
      // 각자 stopPropagation 으로 이 클릭을 막는다.
      onClick={() => onToggleSelected?.(product.id)}
    >
      {/* 우상단 별 — 지금 담지 않아도 나중에 다시 볼 상품을 표시해 둔다. */}
      <Flex
        as={"button"}
        position={"absolute"}
        top={"6px"}
        right={"6px"}
        zIndex={1}
        alignItems={"center"}
        justifyContent={"center"}
        boxSize={"28px"}
        borderRadius={"4px"}
        _hover={{ background: "#F7F9FA" }}
        aria-label={tTitle("favorite")}
        title={tTitle("favorite")}
        onClick={(event) => {
          event.stopPropagation();
          onToggleLike(product.id);
        }}
      >
        <CustomIcon
          src={isLiked ? "star-solid" : "star"}
          width={24}
          height={24}
          color={isLiked ? "#1272EF" : "#8F8F8C"}
        />
      </Flex>
      {onToggleSelected && (
        <Box
          position={"absolute"}
          top={"8px"}
          left={"8px"}
          zIndex={1}
          onClick={(event) => event.stopPropagation()}
        >
          <AppCheckBox
            size={"small"}
            isChecked={isSelected}
            onChange={() => onToggleSelected(product.id)}
          />
        </Box>
      )}
      {/* 사진이 없거나 모자라도 카드 높이가 같도록 2x2 자리를 늘 잡아둔다.
        빈 칸은 아무것도 그리지 않는다(빈 src 는 깨진 이미지로 보인다). */}
      <Grid
        templateColumns={`repeat(${imageColumns}, ${imageSize}px)`}
        templateRows={`repeat(${imageRows}, ${imageSize}px)`}
        gap={`${CARD_GAP}px`}
        // 카드가 이미지 격자보다 넓을 때 이미지가 한쪽으로 쏠리지 않게.
        justifyContent={"center"}
        // 좌상단 체크박스(8px + 16px)와 겹치지 않도록 위를 비워둔다.
        marginTop={onToggleSelected ? "16px" : "0"}
      >
        {cardImages.map((image, index) => (
          <GalleryImage
            key={`${product.id}_image_${index}`}
            url={image.url}
            alt={product.sku || product.name || ""}
            size={imageSize}
            isAi={image.isAi}
            zoomImages={zoomImages}
            showBorder={cardImages.length > 1}
          />
        ))}
      </Grid>

      {/* 브랜드 / 상품(SKU) / 가격은 한 덩어리로 읽히게 붙인다. */}
      <Flex flexDirection={"column"} gap={"3px"} minWidth={0}>
        {/* 브랜드와 SKU 는 같은 상품을 가리키는 두 이름이라 더 바짝 붙인다. */}
        <Flex flexDirection={"column"} gap={"1px"} minWidth={0}>
          <Text
            textStyle={"body-sm-bold"}
            color={"#1D1D1B"}
            noOfLines={1}
            title={product.brandName ?? undefined}
          >
            {product.brandName || "-"}
          </Text>
          <Text
            textStyle={"body-sm-medium"}
            color={"#1D1D1B"}
            noOfLines={1}
            wordBreak={"break-all"}
            cursor={product.sku ? "pointer" : "default"}
            _hover={
              product.sku
                ? { color: "#1272EF", textDecoration: "underline" }
                : undefined
            }
            title={product.sku ?? undefined}
            onClick={(event) => {
              event.stopPropagation();
              onCopySku(product.sku);
            }}
          >
            {product.sku || "-"}
          </Text>
        </Flex>

        {/* 끄면 공급가 한 줄, 켜면 그 값이 어떻게 나왔는지까지 — 도매가·소매가·컨디션.
            이름은 왼쪽, 값은 오른쪽에 맞춰 세로로 읽히게 한다.
            여러 줄이 되면 어디까지가 가격인지 안 보여서 테두리로 묶는다. */}
        <Flex
          flexDirection={"column"}
          gap={"1px"}
          minWidth={0}
          border={showPriceDetail ? "0.5px solid #D4D4D1" : undefined}
          borderRadius={showPriceDetail ? "8px" : undefined}
          padding={showPriceDetail ? "8px 10px" : undefined}
          background={showPriceDetail ? "#FFFFFF" : undefined}
        >
          {showPriceDetail ? (
            <>
              {priceRow(tTitle("cost"), price(product.costPrice))}
              {priceRow(tTitle("retail"), price(product.retailPrice))}
              {priceRow(tTitle("condition"), conditionLabel())}
              {priceRow(
                tTitle("estimated_supply_price"),
                price(product.supplyPrice),
                true
              )}
            </>
          ) : (
            // 한 줄뿐이면 이름을 붙일 이유가 없다 — 금액만 왼쪽에 둔다.
            <Text textStyle={"caption-bold"} color={"#1D1D1B"} noOfLines={1}>
              {price(product.supplyPrice)}
            </Text>
          )}
        </Flex>

        {/* 크림·포이즌 시세(패스픽 멤버만) — 패스픽 상품 카드의 비교 블록을 그대로 쓴다.
            배치(나란히/위아래)는 카드 보기 설정에서 고른다. */}
        {marketPrice && (
          <OrderSheetMarketComparison
            product={product}
            marketPrice={marketPrice}
            pricing={marketPricing}
            layout={marketComparisonLayout}
            onOpenSizes={
              onOpenMarketSizes ? () => onOpenMarketSizes(product) : undefined
            }
          />
        )}
      </Flex>

      {/* 옵션은 기본으로 접어둔다 — 주문할 상품만 펼쳐서 수량을 넣는다.
          이 줄은 어디를 눌러도 펼쳐진다(화살표만 노리게 하지 않는다). 카드 클릭은 선택이라
          여기서 stopPropagation 으로 끊는다. */}
      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={`${CARD_GAP}px`}
        cursor={showToggle ? "pointer" : "default"}
        onClick={(event) => {
          if (!showToggle) return;
          event.stopPropagation();
          onToggle(product.id);
        }}
      >
        <Flex gap={`${CARD_GAP}px`} alignItems={"baseline"} minWidth={0}>
          <Text textStyle={"caption-medium"} color={"#1D1D1B"}>
            {tTitle("ordering")}
          </Text>
          {/* 담은 게 없으면 "-", 있으면 금액만 보여준다(개수는 옵션 표에 있다). */}
          <Text
            textStyle={"body-sm-bold"}
            color={totalQuantity === 0 ? "#8F8F8C" : "#1272EF"}
            noOfLines={1}
          >
            {totalQuantity === 0 ? "-" : orderedAmount()}
          </Text>
        </Flex>
        {/* 상단에서 전체를 펼친 상태면 카드마다 버튼을 둘 필요가 없다.
            글자 버튼은 카드 폭을 잡아먹어서 화살표 하나로 줄였다. */}
        {showToggle && (
          <Flex
            as={"button"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            boxSize={"22px"}
            borderRadius={"4px"}
            border={"0.5px solid #D4D4D1"}
            background={"#FCFCFB"}
            _hover={{ borderColor: "#1272EF" }}
            aria-label={tTitle(isExpanded ? "fold_more" : "option")}
            title={tTitle(isExpanded ? "fold_more" : "option")}
            // 클릭은 이 줄 전체가 받는다 — 여기서 또 토글하면 두 번 뒤집힌다.
            tabIndex={-1}
          >
            <CustomIcon
              src={isExpanded ? "chevron-up" : "chevron-down"}
              width={12}
              height={12}
              color={"#444440"}
            />
          </Flex>
        )}
      </Flex>

      {isExpanded && (
        <Flex
          flexDirection={"column"}
          gap={`${CARD_GAP}px`}
          padding={"8px 10px"}
          // 수량을 만지다가 카드가 선택/해제되면 안 된다.
          onClick={(event) => event.stopPropagation()}
          border={"0.5px solid #D4D4D1"}
          borderRadius={"8px"}
          background={"#FFFFFF"}
        >
          {isEditable && (
            <Flex justifyContent={"flex-end"}>
              <AppButton
                size={"small"}
                height={"22px"}
                isDisabled={isTakenAll}
                onClick={(event) => {
                  event.stopPropagation();
                  onTakeAll(product);
                }}
              >
                {tTitle("take_all")}
              </AppButton>
            </Flex>
          )}
          {/* 옵션명·재고·수량을 열로 맞춘 작은 표 — 이름이 길어도 잘리지 않는다. */}
          <Grid
            templateColumns={"1fr 36px 52px"}
            columnGap={`${CARD_GAP}px`}
            rowGap={`${CARD_GAP}px`}
            alignItems={"center"}
          >
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {tTitle("size")}
            </Text>
            <Text
              textStyle={"caption-medium"}
              color={"#8F8F8C"}
              textAlign={"right"}
            >
              {tTitle("stock")}
            </Text>
            <Text
              textStyle={"caption-medium"}
              color={"#8F8F8C"}
              textAlign={"right"}
            >
              {tTitle("quantity.abbr")}
            </Text>
            {product.orderQuantityWithOptionList.map((option, index) => {
              // 주문서의 잔여재고가 먼저고, 없으면 라인시트 재고로 떨어진다.
              const stock =
                product.newStockWithOptionList?.[index]?.quantity ??
                product.stockQuantityWithOptionList?.[index]?.quantity ??
                null;
              const isSoldOut = stock === 0;
              return (
                <React.Fragment key={`${product.id}_${option.name}_${index}`}>
                  <Text
                    textStyle={"body-sm-bold"}
                    color={isSoldOut ? "#8F8F8C" : "#1D1D1B"}
                    noOfLines={1}
                    wordBreak={"break-all"}
                    title={option.name}
                  >
                    {option.name}
                  </Text>
                  <Text
                    textStyle={"body-sm-medium"}
                    color={isSoldOut ? "#CC1D1D" : "#1D1D1B"}
                    textAlign={"right"}
                  >
                    {/* -1 = 재고 미추적(무제한) */}
                    {stock == null ? "-" : stock === -1 ? "∞" : stock}
                  </Text>
                  <Input
                    type={"number"}
                    min={0}
                    height={"24px"}
                    padding={"0 4px"}
                    fontSize={"12px"}
                    textAlign={"right"}
                    borderColor={"#D4D4D1"}
                    background={isSoldOut ? "#EFEFED" : "#FFFFFF"}
                    value={option.quantity ?? 0}
                    isDisabled={!isEditable || isSoldOut}
                    // 기본값 0이 들어가 있어서, 누르면 통째로 선택해 바로 덮어쓸 수 있게 한다.
                    onFocus={(event) => event.target.select()}
                    onChange={(event) =>
                      onQuantityChange(product, index, event.target.value)
                    }
                  />
                </React.Fragment>
              );
            })}
          </Grid>
        </Flex>
      )}
    </Flex>
  );
});

/**
 * 주문서 상품을 한눈에 훑는 뷰.
 *
 * 편집용 그리드(AG Grid)는 상품 하나가 3행이라 화면에 몇 개 안 들어온다.
 * 여기서는 수량·편집을 빼고 이미지를 크게, 값은 브랜드/SKU/가격/컨디션만 보여준다.
 * 상태는 그리드와 같은 inflatedTabs를 읽으므로 편집 중인 컨디션도 그대로 반영된다.
 */
export default function OrderSheetProductGallery({
  sheetKey,
  tabIndex,
  isLoading = false,
  isEditable = false,
  priceConfig,
  onUpdatingInflatedProductComplete,
  selectedProductIds,
  onSelectedProductIdsChange,
  selectionActions,
  imageCount = DEFAULT_IMAGE_COUNT,
  showPriceDetail = false,
  isAllExpanded = false,
  likedOnly = false,
  currency,
  marketPriceByProductId,
  marketPricing,
  marketComparisonLayout = "stack",
}: Props) {
  const { t, tTitle } = useI18nHelper();
  // 사이즈별 시세 모달 — 열려 있는 상품. 카드가 넘겨준 상품을 그대로 쓴다.
  const [marketSizeProduct, setMarketSizeProduct] =
    useState<InflatedProduct | null>(null);
  const {
    warning: showWarning,
    info: showInfo,
    error: showError,
  } = useAppToasts();
  const { updateInflatedProduct } = useInflatedTabs();
  const inflatedTabs = useRecoilValue(inflatedTabsFamily(sheetKey));
  const imageColumns = IMAGE_COLUMNS[imageCount];
  const imageSize = IMAGE_SIZES[imageCount];
  const imageRows = Math.ceil(imageCount / imageColumns);
  // 카드 폭은 이미지 격자 폭을 따라간다 — 사진이 적으면 카드도 좁아져 더 많이 들어온다.
  // 시세 비교 블록이 있으면 패스픽 카드와 같은 최소 폭을 지킨다 — 나란히 보기는 두 마켓이
  // 좌우로 놓여 240px, 위아래 보기는 200px 아래로 내려가면 라벨과 금액이 접힌다.
  const marketMinCardWidth = marketPriceByProductId
    ? MARKET_MIN_CARD_WIDTH[marketComparisonLayout]
    : 0;
  const minCardWidth = Math.max(
    imageColumns * imageSize + (imageColumns - 1) * CARD_GAP + CARD_PADDING,
    MIN_CARD_WIDTH_FLOOR,
    marketMinCardWidth
  );

  // 옵션은 기본으로 접어두고, 주문할 상품만 펼친다(카드가 짧아져 한 화면에 많이 들어온다).
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const { isLiked: isProductLiked, toggle: toggleProductLike } =
    useOrderSheetProductLikes(sheetKey, tabIndex);

  const selectedIdSet = useMemo(
    () => new Set(selectedProductIds ?? []),
    [selectedProductIds]
  );

  const toggleSelected = useCallback(
    (productId: number) => {
      if (!onSelectedProductIdsChange) return;
      const next = new Set(selectedProductIds ?? []);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      onSelectedProductIdsChange(Array.from(next));
    },
    [selectedProductIds, onSelectedProductIdsChange]
  );

  // 전체 펼치기를 끄면 개별로 펼쳐둔 것도 같이 접는다(설정과 화면이 어긋나지 않게).
  useEffect(() => {
    if (!isAllExpanded) setExpandedIds(new Set());
  }, [isAllExpanded]);

  const toggleExpanded = useCallback((productId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  // 그리드(onCellValueChanged)와 같은 규칙 — 음수·NaN은 0으로, 가용재고를 넘으면 거절.
  // 재고 -1은 재고 미추적이라 상한이 없다.
  const handleQuantityChange = useCallback(
    (product: InflatedProduct, optionIndex: number, rawValue: string) => {
      let nextValue = parseInt(rawValue, 10);
      if (isNaN(nextValue) || nextValue < 0) {
        nextValue = 0;
      }
      const stock = product.newStockWithOptionList
        ? product.newStockWithOptionList[optionIndex]?.quantity
        : null;
      if (stock != null && stock !== -1 && nextValue > stock) {
        showWarning(
          t("common:apply_order_quantity.messages.error.out_of_range"),
          "apply_price_condition_form"
        );
        return;
      }

      const nextProduct = produce(product, (draft) => {
        if (!draft.orderQuantityWithOptionList[optionIndex]) return;
        draft.orderQuantityWithOptionList[optionIndex].quantity = nextValue;
      });
      updateInflatedProduct(sheetKey, tabIndex, nextProduct, priceConfig);
      onUpdatingInflatedProductComplete?.(true);
    },
    [
      sheetKey,
      tabIndex,
      priceConfig,
      updateInflatedProduct,
      onUpdatingInflatedProductComplete,
      showWarning,
      t,
    ]
  );

  // 탭이 아직 recoil 에 안 들어왔는지(로딩 중) 필터 결과가 0건인지 구분해야 한다.
  const tab = useMemo(
    () => inflatedTabs?.tabs.find((i) => i.index === tabIndex),
    [inflatedTabs, tabIndex]
  );

  const products = useMemo((): InflatedProduct[] => {
    // isFiltered=true 가 '필터를 통과한(보여줄) 상품'이다 — 그리드 rowData 와 같은 기준.
    // (이름과 달리 '걸러진'이 아니라 '통과한' 쪽이라 뒤집어 쓰면 안 된다)
    return (
      tab?.products.filter(
        (product) =>
          product.isFiltered && (!likedOnly || isProductLiked(product.id))
      ) ?? []
    );
  }, [tab, likedOnly, isProductLiked]);

  // 하단 주문 통계(OrderSheetTabBottomBar)는 그리드와 같은 컴포넌트를 그대로 쓴다.
  // 넘기는 값도 그리드(buyer-order-sheet.tsx)와 같은 규칙으로 만든다.
  const { value: query } = useOrderSheetSetPagePersistedQuery();
  const isFiltered = isSheetTabFilterActive(query);
  // 시트 통화가 비어 있으면 실제 상품 가격의 통화로 폴백해 심볼과 값을 일치시킨다.
  const { selectedCurrency, exchangeCurrencyValue } = useOrderSheetCurrency(
    currency ||
      tab?.products.find((product) => product.supplyPrice?.currency)
        ?.supplyPrice?.currency
  );

  const bottomBar = (
    <OrderSheetTabBottomBar
      sheetKey={sheetKey}
      exchangeCurrencyValue={exchangeCurrencyValue}
      selectedCurrency={selectedCurrency}
      checkedProductIds={selectedProductIds ?? []}
      isFiltered={isFiltered}
      orderStatus={"ORDERED"}
      commission={priceConfig?.commission}
      commissionType={priceConfig?.commissionType}
    />
  );

  // 그리드의 TakeAllProductsCell 과 같은 규칙 — 옵션마다 재고만큼 채우되,
  // 이미 그보다 많이 넣었으면 그대로 둔다.
  const handleTakeAll = useCallback(
    (product: InflatedProduct) => {
      const stockList =
        product.newStockWithOptionList || product.stockQuantityWithOptionList;
      if (!stockList) return;

      const nextProduct = produce(product, (draft) => {
        const orderList = draft.orderQuantityWithOptionList;
        draft.orderQuantityWithOptionList = stockList.map(
          (stockOption, index) => {
            const ordered = orderList[index]?.quantity ?? 0;
            return {
              ...orderList[index],
              name: stockOption.name,
              quantity:
                ordered < stockOption.quantity ? stockOption.quantity : ordered,
            };
          }
        );
      });
      updateInflatedProduct(sheetKey, tabIndex, nextProduct, priceConfig);
      onUpdatingInflatedProductComplete?.(true);
    },
    [
      sheetKey,
      tabIndex,
      priceConfig,
      updateInflatedProduct,
      onUpdatingInflatedProductComplete,
    ]
  );

  // 모든 옵션이 이미 재고만큼(또는 그 이상) 담겨 있으면 더 담을 게 없다.
  const isTakenAll = (product: InflatedProduct) => {
    const stockList =
      product.newStockWithOptionList || product.stockQuantityWithOptionList;
    if (!stockList || stockList.length === 0) return true;
    return stockList.every((stockOption, index) => {
      const ordered = product.orderQuantityWithOptionList[index]?.quantity ?? 0;
      return ordered >= stockOption.quantity;
    });
  };

  // SKU 를 눌러 복사한다 — 상품 찾기 카드와 같은 동작.
  const handleCopySku = useCallback(
    async (sku: string | null) => {
      if (!sku) return;
      try {
        await navigator.clipboard.writeText(sku);
        showInfo(t("common:sku_copied"));
      } catch (err) {
        // clipboard API 는 비보안 컨텍스트/권한 거부 시 throw 한다.
        showError(err);
      }
    },
    [showInfo, showError, t]
  );

  // "DISCOUNT 10%" + IVA 적용 시 계산식까지. IVA는 DISCOUNT에서만 의미가 있다.
  const conditionLabel = (product: InflatedProduct) => {
    const condition = product.priceAdjustedBy;
    if (!condition?.by) return "-";
    const base = `${condition.by} ${condition.value ?? 0}%`;
    if (condition.by !== "DISCOUNT") return base;
    // 공급가 뒤 괄호 안에 들어가므로 IVA 는 괄호를 겹치지 않게 가운뎃점으로 잇는다.
    if (condition.applyIva === false) return `${base} · IVA -`;
    return `${base} · IVA ${condition.ivaValue || "/1.22"}`;
  };

  // ── 가상 스크롤 ───────────────────────────────────────────────
  // 상품이 수백 개면 카드를 다 그리는 것만으로 느려진다. 화면에 보이는 줄만 그린다.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // 저장하면 로딩 스피너가 잠깐 뜨면서 이 영역이 통째로 사라졌다가 '새 DOM 노드'로 다시 생긴다.
  // mount 때 한 번만 관찰하면 옛 노드에 묶인 채 폭이 0으로 남아 한 줄에 카드가 1장만 보인다.
  // ref 콜백으로 노드가 바뀔 때마다 다시 붙인다.
  const setScrollElement = useCallback((element: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    scrollRef.current = element;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      setContainerWidth(element.clientWidth);
    });
    observer.observe(element);
    observerRef.current = observer;
    setContainerWidth(element.clientWidth);
  }, []);

  // 한 줄에 들어가는 카드 수 — 폭을 모르는 첫 렌더에는 1로 두고 측정 후 다시 잡는다.
  const columnCount = Math.max(
    1,
    Math.floor(
      (containerWidth - CONTENT_PADDING * 2 + CARD_GAP) /
        (minCardWidth + CARD_GAP)
    )
  );

  const rows = useMemo(() => {
    const chunks: InflatedProduct[][] = [];
    for (let i = 0; i < products.length; i += columnCount) {
      chunks.push(products.slice(i, i + columnCount));
    }
    return chunks;
  }, [products, columnCount]);

  // 대략적인 줄 높이 — 이미지 영역 + 텍스트 세 줄 + 주문 줄 + 카드 여백.
  const estimatedRowHeight =
    imageRows * imageSize + (imageRows - 1) * CARD_GAP + 120;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 2,
  });

  // 전체 선택은 '지금 보이는(필터 통과) 상품' 기준이다 — 그리드의 헤더 체크박스와 같은 범위.
  const visibleIds = products.map((product) => product.id);
  const selectedVisibleCount = visibleIds.filter((id) =>
    selectedIdSet.has(id)
  ).length;
  const isAllSelected =
    visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;

  const toggleSelectAll = () => {
    if (!onSelectedProductIdsChange) return;
    onSelectedProductIdsChange(isAllSelected ? [] : visibleIds);
  };

  // 응답이 온 뒤에도 recoil 에 탭이 채워지기까지 한 박자 있다. 그 사이를 '결과 없음'으로
  // 보여주면 상품이 나타나기 직전에 '결과가 없습니다'가 한 번 깜빡인다.
  if (isLoading || !tab) {
    return <CenteredSpinner />;
  }

  if (products.length === 0) {
    return (
      <Flex flexDirection={"column"} width={"100%"} flexGrow={1} minHeight={0}>
        <Flex
          width={"100%"}
          flexGrow={1}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {t("common:no_results")}
          </Text>
        </Flex>
        {bottomBar}
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection={"column"}
      width={"100%"}
      flexGrow={1}
      // 부모가 높이를 잡아주고 안쪽 격자가 스크롤한다.
      minHeight={0}
    >
      {/* 상품을 고르면 컨디션 적용이 붙어 줄이 높아진다 — 그만큼 미리 비워 두어야
          선택할 때마다 카드가 아래로 밀리지 않는다(28px = 컨디션 입력 높이). */}
      <Flex
        gap={`${CARD_GAP}px`}
        alignItems={"center"}
        padding={"8px 16px 0 16px"}
        minHeight={"36px"}
      >
        {/* 전체 선택 — 상단 일괄 적용(컨디션·IVA)에 쓰는 선택과 같은 값이다. */}
        <AppCheckBox
          size={"small"}
          isChecked={isAllSelected}
          isIndeterminate={!isAllSelected && selectedVisibleCount > 0}
          name={tTitle("select_all")}
          onChange={toggleSelectAll}
        />
        {/* 고른 상품에 하는 일(컨디션 적용·다운로드)은 고르는 자리 바로 옆에 둔다. */}
        {selectionActions}
      </Flex>
      <Box
        ref={setScrollElement}
        flexGrow={1}
        minHeight={0}
        overflowY={"auto"}
        padding={`8px ${CONTENT_PADDING}px 16px ${CONTENT_PADDING}px`}
      >
        {/* 보이는 줄만 그린다 — 전체 높이는 가상 스크롤러가 잡아준다. */}
        <Box
          position={"relative"}
          width={"100%"}
          height={`${virtualizer.getTotalSize()}px`}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => (
            <Box
              key={String(virtualRow.key)}
              data-index={virtualRow.index}
              // 옵션을 펼치면 줄 높이가 달라지므로 실제 높이를 다시 잰다.
              ref={virtualizer.measureElement}
              position={"absolute"}
              top={0}
              left={0}
              width={"100%"}
              transform={`translateY(${virtualRow.start}px)`}
            >
              <Grid
                // 카드 폭을 못 박으면 오른쪽에 못 채운 자리가 남는다 — 남는 폭을
                // 카드들이 나눠 갖게 한다(칸 수는 minCardWidth 로 이미 정해졌다).
                templateColumns={`repeat(${columnCount}, minmax(0, 1fr))`}
                gap={`${CARD_GAP}px`}
                paddingBottom={`${CARD_GAP}px`}
              >
                {rows[virtualRow.index]?.map((product) => (
                  <GalleryCard
                    key={product.id}
                    product={product}
                    imageCount={imageCount}
                    imageColumns={imageColumns}
                    imageRows={imageRows}
                    imageSize={imageSize}
                    isExpanded={isAllExpanded || expandedIds.has(product.id)}
                    isSelected={selectedIdSet.has(product.id)}
                    isLiked={isProductLiked(product.id)}
                    onToggleLike={toggleProductLike}
                    showPriceDetail={showPriceDetail}
                    onToggleSelected={
                      onSelectedProductIdsChange ? toggleSelected : undefined
                    }
                    showToggle={!isAllExpanded}
                    isEditable={isEditable}
                    onToggle={toggleExpanded}
                    onQuantityChange={handleQuantityChange}
                    onTakeAll={handleTakeAll}
                    onCopySku={handleCopySku}
                    marketPrice={marketPriceByProductId?.get(product.id)}
                    onOpenMarketSizes={
                      marketPriceByProductId ? setMarketSizeProduct : undefined
                    }
                    marketComparisonLayout={marketComparisonLayout}
                    marketPricing={marketPricing}
                  />
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
      {bottomBar}
      {marketSizeProduct && (
        <OrderSheetMarketSizeModal
          product={marketSizeProduct}
          marketPrice={
            marketPriceByProductId?.get(marketSizeProduct.id) ?? null
          }
          pricing={marketPricing}
          onClose={() => setMarketSizeProduct(null)}
        />
      )}
    </Flex>
  );
}
